import React, { useRef } from 'react'
import { Card, Typography, Flex, Tag, Button } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import type { FlowStep } from '@/data'
import { getStepType } from './config'
import { DebtQuestionEditor } from '../shared/DebtQuestionEditor'
import { QualificationEditor } from '../shared/QualificationEditor'
import { VerificationEditor } from './VerificationEditor'
import { FormStepEditor } from '../shared/FormStepEditor'

const { Text } = Typography

interface FindingAssistanceBuilderProps {
  steps: FlowStep[]
  onChange: (steps: FlowStep[]) => void
  onDeleteStep?: (stepId: number) => void
}

export const FindingAssistanceBuilder: React.FC<
  FindingAssistanceBuilderProps
> = ({ steps, onChange, onDeleteStep }) => {
  const idCounter = useRef(0)

  // Steps are: [debt-question, ...qualifications, verification, ...formSteps]
  const debtStep = steps[0]
  const verificationIndex = steps.findIndex(
    (s, i) => i > 0 && getStepType(s) === 'verification',
  )
  const qualificationSteps = steps.slice(1, verificationIndex)
  const verificationStep = steps[verificationIndex]
  const formSteps = steps.slice(verificationIndex + 1)

  const updateStep = (index: number, updated: FlowStep) => {
    const newSteps = [...steps]
    newSteps[index] = updated
    onChange(newSteps)
  }

  const addQualificationStep = () => {
    const n = ++idCounter.current
    const newStep: FlowStep = {
      ordinal: steps.length,
      headline: 'New Qualification Step',
      sub_headline: 'Enter a sub headline',
      cta_button: '',
      inputs: [
        {
          id: `btn_yes_${n}`,
          label: 'Yes',
          type: 'radio',
          placeholder: null,
          required: false,
          action: 'progress',
        },
        {
          id: `btn_no_${n}`,
          label: 'No',
          type: 'radio',
          placeholder: null,
          required: false,
          action: 'downsell',
        },
      ],
    }
    // Insert before verification
    const newSteps = [...steps]
    newSteps.splice(verificationIndex, 0, newStep)
    onChange(newSteps.map((s, i) => ({ ...s, ordinal: i + 1 })))
  }

  const removeQualificationStep = (qualIndex: number) => {
    // qualIndex is relative to qualificationSteps; actual index is qualIndex + 1
    const actualIndex = qualIndex + 1
    const stepToRemove = steps[actualIndex]
    if (stepToRemove.id != null) onDeleteStep?.(stepToRemove.id)
    onChange(steps.filter((_, i) => i !== actualIndex))
  }

  const updateFormSteps = (newFormSteps: FlowStep[]) => {
    onChange([
      debtStep,
      ...qualificationSteps,
      verificationStep,
      ...newFormSteps,
    ])
  }

  const stepTagColor = (type: string) => {
    switch (type) {
      case 'debt-question':
        return 'red'
      case 'qualification':
        return 'blue'
      case 'verification':
        return 'orange'
      default:
        return 'default'
    }
  }

  const stepLabel = (type: string) => {
    switch (type) {
      case 'debt-question':
        return 'Debt Question'
      case 'qualification':
        return 'Qualification'
      case 'verification':
        return 'Verification'
      default:
        return type
    }
  }

  return (
    <Card title="Finding Assistance Steps" style={{ maxWidth: 1100 }}>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Text type="secondary">
          Configure the Finding Assistance funnel. The debt question is always
          first, verification is always after qualifications. You can add or
          remove qualification and form steps.
        </Text>

        {/* Debt Question — locked first */}
        <Card
          size="small"
          title={
            <Flex gap="small" align="center">
              <Tag color={stepTagColor('debt-question')}>Step 1</Tag>
              <Text strong>{stepLabel('debt-question')}</Text>
            </Flex>
          }
          style={{ backgroundColor: '#fafafa' }}
        >
          <DebtQuestionEditor
            step={debtStep}
            onChange={(updated) => updateStep(0, updated)}
          />
        </Card>

        {/* Qualification steps — add/remove allowed */}
        {qualificationSteps.map((qStep, qualIndex) => (
          <Card
            key={qStep.id ?? qualIndex}
            size="small"
            title={
              <Flex gap="small" align="center">
                <Tag color={stepTagColor('qualification')}>
                  Step {qualIndex + 2}
                </Tag>
                <Text strong>{stepLabel('qualification')}</Text>
              </Flex>
            }
            extra={
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeQualificationStep(qualIndex)}
                size="small"
              >
                Remove
              </Button>
            }
            style={{ backgroundColor: '#fafafa' }}
          >
            <QualificationEditor
              step={qStep}
              onChange={(updated) => updateStep(qualIndex + 1, updated)}
            />
          </Card>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addQualificationStep}
          style={{ width: '100%' }}
        >
          Add Qualification Step
        </Button>

        {/* Verification — locked position */}
        <Card
          size="small"
          title={
            <Flex gap="small" align="center">
              <Tag color={stepTagColor('verification')}>
                Step {verificationIndex + 1}
              </Tag>
              <Text strong>{stepLabel('verification')}</Text>
            </Flex>
          }
          style={{ backgroundColor: '#fafafa' }}
        >
          <VerificationEditor
            step={verificationStep}
            onChange={(updated) => updateStep(verificationIndex, updated)}
          />
        </Card>

        {/* Form Steps — multiple, after verification */}
        <FormStepEditor
          formSteps={formSteps}
          onChange={updateFormSteps}
          onDeleteStep={onDeleteStep}
        />

        <Text type="secondary" style={{ fontSize: 12 }}>
          {steps.length} step{steps.length !== 1 ? 's' : ''} configured
          {qualificationSteps.length > 0 &&
            ` (${qualificationSteps.length} qualification)`}
          {formSteps.length > 0 && ` (${formSteps.length} form)`}
        </Text>
      </Flex>
    </Card>
  )
}
